import { PauseIcon, PlayIcon } from '@heroicons/react/24/solid';
import { useEffect, useRef, useState } from 'react';
import type { MarketingMedia } from '../../lib/marketing';
import { Button, EmptyState, MediaFrame } from '../ui';

interface ProductVideoProps {
  media: MarketingMedia;
  eager?: boolean;
}

function useReducedMotion(): boolean {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return reducedMotion;
}

export function ProductVideo({ media, eager = false }: ProductVideoProps): JSX.Element {
  const containerRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const reducedMotion = useReducedMotion();
  const [isNearViewport, setIsNearViewport] = useState(eager);
  const [isVisible, setIsVisible] = useState(eager);
  const [pausedByVisitor, setPausedByVisitor] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        setIsVisible(entry.isIntersecting);
        if (entry.isIntersecting) setIsNearViewport(true);
      },
      { rootMargin: '240px 0px', threshold: 0.25 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || reducedMotion || videoFailed) return;

    if (isVisible && !pausedByVisitor) {
      video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }, [isVisible, pausedByVisitor, reducedMotion, videoFailed]);

  const showPoster = reducedMotion || videoFailed || !isNearViewport;
  const showUnavailable = showPoster && posterFailed;

  const canControlPlayback = !reducedMotion && !videoFailed && isNearViewport;

  return (
    <MediaFrame
      ref={containerRef}
      label="Real product preview"
      scrim
      controls={
        canControlPlayback ? (
          // Story 2-147xd: left as a direct `Button` — a media SCRIM overlay
          // control (`MediaFrame`'s own `controls` slot), coupled to that
          // component's `group`/`group-hover` class on the ANCESTOR element
          // for its hover/focus-visible reveal transition. No governed host
          // renders inside a hover-scrim overlay, and no other flagged call
          // site shares this shape (fixed circular hit target, opacity
          // transition keyed off a group ancestor) — a genuine one-off.
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            icon={pausedByVisitor ? <PlayIcon className="h-4 w-4" /> : <PauseIcon className="h-4 w-4" />}
            onClick={() => setPausedByVisitor((current) => !current)}
            aria-label={pausedByVisitor ? `Play ${media.title}` : `Pause ${media.title}`}
            className="h-10 w-10 rounded-full border border-border bg-surface-contrast p-0 opacity-100 backdrop-blur-md transition sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
          >
            {pausedByVisitor ? `Play ${media.title}` : `Pause ${media.title}`}
          </Button>
        ) : null
      }
    >
      {showUnavailable ? (
        <div className="flex h-full items-center justify-center">
          <EmptyState
            title="Preview unavailable"
            description="Product preview is temporarily unavailable."
          />
        </div>
      ) : showPoster ? (
        <img
          src={media.poster}
          alt={media.alt}
          loading={eager ? 'eager' : 'lazy'}
          onError={() => setPosterFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          autoPlay={!pausedByVisitor}
          preload={eager ? 'metadata' : 'none'}
          poster={media.poster}
          aria-label={media.alt}
          onCanPlay={() => {
            if (!pausedByVisitor && isVisible) videoRef.current?.play().catch(() => undefined);
          }}
          onError={() => setVideoFailed(true)}
          className="h-full w-full object-cover"
        >
          {/* One well-encoded H.264 master (story 4-41) — the webm it replaced was
              half the bitrate and lost the fight on screen-recorded UI. */}
          <source src={media.mp4} type="video/mp4" />
        </video>
      )}
    </MediaFrame>
  );
}
